<template>
  <CalicoOnboarding
    ref="onboarding"
    :loading="loading"
    :form-data="search"
    :error="errorMessage"
    @save="save"
  >
    <div class="claim-business">
      <div class="header">
        <div class="body--large">
          {{ ClaimBusinessMessage.FORM_TITLE }}
        </div>
        <div class="body--medium">
          {{ ClaimBusinessMessage.FORM_SUBTITLE }}
        </div>
      </div>

      <CalicoRequiredFormHeader />

      <div class="form">
        <CalicoForm
          v-slot="{ field, error }"
          name="name"
          label="Business Name"
          rules="required"
        >
          <CalicoField
            v-model="search.name"
            v-bind="field"
            :error="!!error"
            placeholder="Business Name"
          />
        </CalicoForm>

        <CalicoForm
          v-slot="{ field, error }"
          name="email"
          label="Business Email"
          rules="required|email"
        >
          <CalicoField
            v-model="search.email"
            v-bind="field"
            :error="!!error"
            placeholder="email@example.com"
          />
        </CalicoForm>

        <p class="helper body--xsmall text-secondary">
          {{ ClaimBusinessMessage.EMAIL_HELPER }}
        </p>

        <CalicoForm
          v-slot="{ field, error }"
          name="phone"
          label="Business Phone"
          rules="required"
        >
          <CalicoField
            v-model="search.phone"
            v-bind="field"
            :error="!!error"
            placeholder="(555) 555-5555"
          />
        </CalicoForm>

        <p class="helper body--xsmall text-secondary">
          {{ ClaimBusinessMessage.PHONE_HELPER }}
        </p>

        <CalicoForm
          v-slot="{ field, error }"
          name="street"
          label="Business Address"
          rules="required"
        >
          <CalicoField
            v-model="search.street"
            v-bind="field"
            :error="!!error"
            placeholder="ex. 1234 Main St"
          />
        </CalicoForm>

        <CalicoForm
          v-slot="{ field, error }"
          name="address2"
          label="Address 2"
          rules=""
        >
          <CalicoField
            v-model="search.address2"
            v-bind="field"
            :error="!!error"
            placeholder="Bldg, Ste, Unit, etc."
          />
        </CalicoForm>

        <CalicoForm
          v-slot="{ field, error }"
          name="city"
          label="City"
          rules="required"
        >
          <CalicoField
            v-model="search.city"
            v-bind="field"
            :error="!!error"
            placeholder="City"
          />
        </CalicoForm>

        <CalicoForm
          v-slot="{ field, error }"
          name="state"
          label="State"
          rules="required"
        >
          <CalicoDropdown
            v-model="search.state"
            v-bind="field"
            :error="!!error"
            placeholder="Select..."
          >
            <CalicoDropdownMenuItem
              v-for="option in states"
              :key="option.value"
              class="calico-option body--medium"
              :value="option.value"
            >
              {{ option.label }}
            </CalicoDropdownMenuItem>
          </CalicoDropdown>
        </CalicoForm>

        <CalicoForm
          v-slot="{ field, error }"
          name="zip"
          label="Zip"
          rules="required|zip"
        >
          <CalicoField
            v-model="search.zip"
            v-bind="field"
            :error="!!error"
            maxlength="5"
            :only-numbers="true"
            placeholder="Zip" 
          />
        </CalicoForm>
      </div>
    </div>

    <Modal 
      v-if="show" 
      class="claim-business-modal"
      :header="ClaimBusinessMessage.ERROR_TITLE" 
      @close="show = false"
    >
      <template #content>
        <div class="confirmation">
          <i class="bi bi-x-lg contained-icon-large" />
          <h1 class="heading--large">
            {{ ClaimBusinessMessage.ERROR_TITLE }}
          </h1>
          <p class="body--medium">
            {{ ClaimBusinessMessage.ERROR_BODY }}
            <br><br>
            <u>{{ ClaimBusinessMessage.ERROR_FOOTER }}</u>
          </p>
        </div>
      </template>
      <template #footer>
        <button
          class="button-utility"
          @click="show = false"
        >
          {{ ClaimBusinessMessage.CTA_CLOSE }}
        </button>
      </template>
    </Modal>

    <Modal
      v-if="showBusinessConfirmation"
      class="claim-business-modal"
      :header="ClaimBusinessMessage.MODAL_TITLE"
      @close="cancelClaim"
    >
      <template #content>
        <div class="confirmation-container">
          <div class="label--medium text-highlight">
            {{ ClaimBusinessMessage.YOU_ENTERED }}
          </div>

          <div class="info-container">
            <div class="body--large">
              {{ search.name }}<br>
              {{ search.email }}<br>
              {{ search.phone }}<br>
              {{ search.street }} {{ search.address2 }}<br>
              {{ search.city }}, {{ search.state }} {{ search.zip }}
            </div>
          </div>


          <!-- FOUND -->
          <template v-if="confirmationType === 'FOUND'">
            <div class="label--medium text-highlight">
              {{ ClaimBusinessMessage.WE_FOUND }}
            </div>

            <ClaimBusinessCard
              :title="foundBusiness.name"
              :address="foundBusiness.street"
              :city="foundBusiness.city"
              :state="foundBusiness.state"
              :zip="foundBusiness.zip"
              :phone-number="foundBusiness.phone"
              :description="foundBusiness.description"
              :image="foundBusiness.businessPhoto"
            />
          </template>

          <!-- NOT FOUND -->
          <template v-else>
            <p class="body--large">
              {{ ClaimBusinessMessage.WE_DID_NOT_FIND }}
            </p>

            <ul class="body--medium">
              <li>{{ ClaimBusinessMessage.NOT_FOUND_HINT_1 }}</li>
              <li>{{ ClaimBusinessMessage.NOT_FOUND_HINT_2 }}</li>
            </ul>
          </template>

          <div class="info-container">
            <CalicoCheckbox
              v-model="confirmed"
              :value="true"
            >
              <span class="body--medium bold">
                {{ ClaimBusinessMessage.CONFIRMATION_LABEL }}
              </span>
            </CalicoCheckbox>

            <p class="body--medium text-secondary modal-warning">
              {{ ClaimBusinessMessage.IRREVERSIBLE_WARNING }}
            </p>
          </div>
        </div>
      </template>


      <template #footer>
        <div class="claim-footer">
          <button
            class="button-primary"
            :disabled="!confirmed"
            @click="finalizeClaim"
          >
            {{ ClaimBusinessMessage.CTA_CLAIM }}
          </button>

          <button
            class="button-secondary"
            @click="cancelClaim"
          >
            {{ ClaimBusinessMessage.CTA_GO_BACK }}
          </button>

          <a
            class="label--medium"
            @click="reportIssue"
          >
            <i class="bi bi-exclamation-triangle-fill" />
            Report an issue
          </a>
        </div>
      </template>
    </Modal>
  </CalicoOnboarding>
</template>

<script>
import CalicoOnboarding from "@/components/template/onboarding/index.vue";
import CalicoRequiredFormHeader from "@/components/calico/formHeader/index.vue";
import CalicoDropdown from '@/components/calico/dropdown/index.vue';
import CalicoDropdownMenuItem from '@/components/calico/dropdownMenuItem/index.vue';
import CalicoField from "@/components/calico/field/index.vue";
import CalicoForm from "@/components/pattern/form/index.vue";
import Modal from "@/components/template/modal/index.vue";
import ClaimBusinessCard from "@/components/calico/claimBusinessCard/index.vue";
import { states } from "@/constants";
import services from "@/services";
import { formatPhone, normalizePhone } from "@/utils/helper";
import { RoutesEnum, ClaimBusinessMessage, ClaimBusinessModal } from "@/enums";
import CalicoCheckbox from "@/components/calico/checkbox/index.vue";

export default {
  name: "ClaimBusinessScreen",
  components: {
    CalicoOnboarding,
    CalicoRequiredFormHeader,
    CalicoField,
    CalicoForm,
    CalicoDropdown,
    CalicoDropdownMenuItem,
    ClaimBusinessCard,
    Modal,
    CalicoCheckbox
  },
  data() {
    return {
      search: {
        name: "",
        email: "",
        phone: "",
        street: "",
        address2: "",
        city: "",
        state: "",
        zip: ""
      },
      states,
      loading: false,
      errorMessage: "",
      show: false,
      showBusinessConfirmation: false,
      foundBusiness: null,
      claimedBusinessId: null,
      nextCallback: null,
      pendingPayload: null,
      confirmed: false,
      confirmationType: null,
      ClaimBusinessMessage,
      ClaimBusinessModal
    };
  },

  watch: {
    "search.phone"(val) {
      const formatted = formatPhone(val);
      if (formatted !== val) this.search.phone = formatted;
    }
  },

  async mounted() {
    try {
      this.loading = true;

      const user = await services.user.get();

      if (user?.business_id) {
        this.$router.replace(RoutesEnum.ENTITY_DETAILS);
        return;
      }

      if (user?.pending_business_claim_id) {
        const businessId = user.pending_business_claim_id;

        this.claimedBusinessId = businessId;
        this.confirmationType = ClaimBusinessModal.FOUND;

        const details = await services.businessClaim.details(businessId);

        this.foundBusiness = {
          ...details.business,
          businessPhoto: details.placeData?.business_photo?.photoUri || null,
          description: details.placeData?.description || ""
        };

        this.search = {
          name: details.business.name || "",
          street: details.business.street || "",
          city: details.business.city || "",
          state: details.business.state || "",
          zip: details.business.zip || "",
          address2: details.business.street2 || "",
          email: details.business.email || "",
          phone: formatPhone(details.business.phone || "")
        };

        this.showBusinessConfirmation = true;
        return;
      }

      const business = await services.user.business.get();

      if (business && business.internal_status !== "UNCLAIMED") {
        this.$router.replace(RoutesEnum.ENTITY_DETAILS);
        return;
      }

      const placesData = business?.placesData?.[0];

      if (placesData) {
        this.search = {
          name: placesData.business_name || "",
          street: placesData.street || "",
          city: placesData.city || "",
          state: placesData.state || "",
          zip: placesData.zip || "",
          address2: business?.address2 || "",
          email: business?.email || "",
          phone: formatPhone(business?.phone || "")
        };
      }

    } catch (error) {
      console.error("Error loading claim state:", error);
    } finally {
      this.loading = false;
    }
  },

  methods: {
    async save(next) {
      this.errorMessage = '';
      this.nextCallback = next;
      this.loading = true;
      this.confirmed = false;
      this.confirmationType = null;
      this.foundBusiness = null;
      this.claimedBusinessId = null;

      this.pendingPayload = {
        ...this.search,
        phone: normalizePhone(this.search.phone),
      };

      try {
        const res = await services.businessClaim.claim(this.pendingPayload);

        this.confirmationType = res.found ? ClaimBusinessModal.FOUND : ClaimBusinessModal.NOT_FOUND;

        if (!res.claimed) {
          this.show = true;
          return;
        }

        if (res.businessId) {
          this.claimedBusinessId = res.businessId;
        }

        if (res.found && res.businessId) {
          this.claimedBusinessId = res.businessId;

          const details = await services.businessClaim.details(res.businessId);
          this.foundBusiness = {
            ...details.business,
            businessPhoto: details.placeData?.business_photo?.photoUri || null,
            description: details.placeData?.description || ""
          };
        }

        this.showBusinessConfirmation = true;

      } catch (error) {
        this.show = true;
      } finally {
        this.loading = false;
      }
    },

    async finalizeClaim() {
      if (!this.confirmed) return;

      try {
        this.loading = true;

        if (this.confirmationType === ClaimBusinessModal.NOT_FOUND) {
          this.showBusinessConfirmation = false;
          this.nextCallback?.();
          return;
        }

        await services.businessClaim.confirm({
          businessId: this.claimedBusinessId
        });

        this.showBusinessConfirmation = false;
        this.nextCallback?.();

      } catch (error) {
        this.showBusinessConfirmation = false;
        this.show = true;
      } finally {
        this.loading = false;
      }
    },

    async cancelClaim() {
      if (!this.claimedBusinessId) {
        this.showBusinessConfirmation = false;
        return;
      }

      try {
        this.loading = true;

        await services.businessClaim.cancel({
          businessId: this.claimedBusinessId,
        });
      } catch (error) {
        console.error("Failed to cancel claim", error);
      } finally {
        this.loading = false;
        this.showBusinessConfirmation = false;
        this.resetClaimForm();
      }
    },

    reportIssue() {
      window.location.href = "mailto:support@betascore.ai";
    },

    getEmptySearch() {
      return {
        name: "",
        email: "",
        phone: "",
        street: "",
        address2: "",
        city: "",
        state: "",
        zip: ""
      };
    },

    resetClaimForm() {
      this.search = this.getEmptySearch();

      this.confirmed = false;
      this.confirmationType = null;
      this.foundBusiness = null;
      this.claimedBusinessId = null;
      this.pendingPayload = null;

      this.$nextTick(() => {
        this.$refs.onboarding?.reset?.();
      });
    },
  },
  
};
</script>

<style scoped lang="scss">
.claim-business {
  @include stack-space($space-large);
  padding: $space-large;
  gap: $space-large;

  .header {
    @include stack-space($space-small);
  }

  .form {
    @include stack-space($space-large);
  }

  .helper {
    margin-top: -$space-small;
  }

  .business-question {
    @include inset-space($space-large);
    @include stack-space($space-none);
    width: 100%;
    max-width: 580px;
    height: 76px;
    border-radius: $radius-large;
    background-color: $color-orange-lightest;
  }

  .text-highlight {
    color: $color-lapis;
  }

  ul {
    @include stack-space($space-xsmall);
    gap: $space-medium;
  }

  li {
    line-height: 1.3;
  }
}

.claim-business-modal {
  .confirmation {
    @include stack-space($space-small);
    gap: $space-large;
    align-items: center;
    justify-content: center;
    padding: $space-xxlarge $space-large;

    i {
      background-color: $color-semantics-error;
      border: none;
      color: $color-white;
    }

    p {
      margin: 0;
      text-align: center;
    }
  }

  .confirmation-container {
    @include stack-space($space-large);
    padding: $space-large;
  }

  .info-container {
    @include inset-space($space-medium);
    @include stack-space($space-small);
    padding: $space-medium;
    gap: $space-medium;
    background-color: $color-neutral-lightest;
    border-radius: $radius-large;
  }

  .modal-warning {
    color: $color-red-dark;
  }

  .claim-footer {
    @include stack-space($space-medium);
    padding: $space-medium;
    text-align: center;

    a {
      cursor: pointer;
    }
  }
}
</style>
